import React, { useState, useContext, useEffect } from "react";
import clsx from "clsx";
import { Sidebar } from "../components/ui";
import NotificationsPopOver from "../components/NotificationsPopOver";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import { useThemeContext } from "../context/DarkMode";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { Can } from "../components/Can";

const Icons = Sidebar.Icons;

const LoggedInLayout = ({ children }) => {
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { handleLogout, loading, user } = useContext(AuthContext);
  const { darkMode, toggleTheme } = useThemeContext();
  const { whatsApps } = useContext(WhatsAppsContext);
  const [connectionWarning, setConnectionWarning] = useState(false);

  // Check for connection warnings
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        setConnectionWarning(offlineWhats.length > 0);
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  // Handle responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Menu items
  const menuItems = [
    {
      to: "/",
      icon: Icons.Dashboard,
      label: "Dashboard",
    },
    {
      to: "/connections",
      icon: Icons.Connection,
      label: i18n.t("mainDrawer.listItems.connections"),
      badge: connectionWarning ? "!" : null,
    },
    {
      to: "/tickets",
      icon: Icons.Chat,
      label: i18n.t("mainDrawer.listItems.tickets"),
    },
    {
      to: "/contacts",
      icon: Icons.Contacts,
      label: i18n.t("mainDrawer.listItems.contacts"),
    },
    {
      to: "/quickAnswers",
      icon: Icons.QuickAnswer,
      label: i18n.t("mainDrawer.listItems.quickAnswers"),
    },
  ];

  // Admin items
  const adminItems = [
    {
      to: "/users",
      icon: Icons.Users,
      label: i18n.t("mainDrawer.listItems.users"),
    },
    {
      to: "/queues",
      icon: Icons.Queues,
      label: i18n.t("mainDrawer.listItems.queues"),
    },
    {
      to: "/settings",
      icon: Icons.Settings,
      label: i18n.t("mainDrawer.listItems.settings"),
    },
  ];

  if (loading) {
    return <BackdropLoading />;
  }

  // Check if user is admin
  const isAdmin = user?.profile === "admin";

  return (
    <div className="min-h-screen bg-dark-50 dark:bg-dark-950">
      {/* Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        menuItems={menuItems}
        adminItems={adminItems}
        showAdmin={isAdmin}
        user={user}
        darkMode={darkMode}
        onToggleTheme={toggleTheme}
        onOpenProfile={() => setUserModalOpen(true)}
        onLogout={handleLogout}
        connectionWarning={connectionWarning}
      />

      {/* User Profile Modal */}
      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />

      {/* Main Content */}
      <div
        className={clsx(
          "min-h-screen transition-all duration-300",
          sidebarOpen ? "md:ml-64" : "md:ml-[72px]"
        )}
      >
        {/* Top Header Bar */}
        <header className="sticky top-0 z-30 h-16 bg-white dark:bg-dark-900 border-b border-dark-200 dark:border-dark-800 px-4 flex items-center justify-between">
          {/* Mobile Menu Button */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg text-dark-500 hover:bg-dark-100 dark:hover:bg-dark-800 md:hidden"
          >
            <Icons.Menu className="w-6 h-6" />
          </button>

          {/* Page Title - can be dynamic */}
          <div className="hidden md:block">
            <h1 className="text-lg font-semibold text-dark-900 dark:text-white">
              WhaTicket
            </h1>
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-2">
            {/* Theme Toggle (visible on mobile) */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-dark-500 hover:bg-dark-100 dark:hover:bg-dark-800 md:hidden"
            >
              {darkMode ? (
                <Icons.Sun className="w-5 h-5" />
              ) : (
                <Icons.Moon className="w-5 h-5" />
              )}
            </button>

            {/* Notifications */}
            {user?.id && <NotificationsPopOver />}

            {/* User Avatar (mobile) */}
            <button
              onClick={() => setUserModalOpen(true)}
              className="p-1 md:hidden"
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-semibold text-sm">
                {user?.name?.charAt(0)?.toUpperCase() || "U"}
              </div>
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="h-[calc(100vh-4rem)]">
          {children}
        </main>
      </div>
    </div>
  );
};

export default LoggedInLayout;
